/**
 * Optimaxx Comparator — Side-by-side "Sin plan vs Con Optimaxx Plus"
 * Lets user adjust monthly contribution and time horizon with sliders,
 * then visually shows the gap between doing nothing vs investing.
 */
import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingDown, TrendingUp, Sparkles } from 'lucide-react';
import ShareButtons from './ShareButtons';
import './OptimaxxComparator.css';

const ANNUAL_RATE = 0.08; // 8% promedio Optimaxx Plus
const LOYALTY_BONUS_TIERS = [
  { min: 8000, bonus: 1.0 },
  { min: 5000, bonus: 0.75 },
  { min: 3000, bonus: 0.65 },
  { min: 2000, bonus: 0.55 },
  { min: 0, bonus: 0 }
];

const formatMoney = (n) => `$${Math.round(n).toLocaleString('es-MX')}`;
const formatCompact = (n) => {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${Math.round(n)}`;
};

const getLoyaltyBonusRate = (monthly) =>
  LOYALTY_BONUS_TIERS.find((t) => monthly >= t.min)?.bonus ?? 0;

const buildComparatorShareText = ({ monthly, years, gap, withPlan }) => {
  const monthlyTxt = `$${Math.round(monthly).toLocaleString('es-MX')}`;
  const gapTxt = `$${Math.round(gap).toLocaleString('es-MX')}`;
  const totalTxt = `$${Math.round(withPlan).toLocaleString('es-MX')}`;
  return `Acabo de calcular: ahorrando ${monthlyTxt}/mes durante ${years} años puedo terminar con ${totalTxt}. Me estoy perdiendo ${gapTxt} solo por seguir diciendo "mañana empiezo" 🤯`;
};

const OptimaxxComparator = ({ onCtaClick }) => {
  const [monthly, setMonthly] = useState(2000);
  const [years, setYears] = useState(25);

  const { totalContributed, withoutPlan, withPlan, loyaltyBonus, gap, chartData } = useMemo(() => {
    const months = years * 12;
    const monthlyRate = ANNUAL_RATE / 12;
    const totalContrib = monthly * months;

    // "Sin plan" — el dinero se gasta. Valor real al final: 0.
    // Visualización: muestra como línea plana en el monto aportado teórico (lo que pudiste haber tenido sin invertir, sin gastar).
    const finalWithoutPlan = totalContrib;

    // "Con Optimaxx Plus" — interés compuesto al 8% + bono de fidelidad al mes 18
    const finalWithPlan = monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
    const bonusRate = getLoyaltyBonusRate(monthly);
    const annualContribution = monthly * 12;
    const loyaltyBonusAmount = annualContribution * bonusRate;
    const finalWithPlanPlusBonus = finalWithPlan + loyaltyBonusAmount * Math.pow(1 + ANNUAL_RATE, years - 1.5);

    const finalGap = finalWithPlanPlusBonus - finalWithoutPlan;

    // Chart data — yearly snapshots
    const data = [];
    for (let y = 0; y <= years; y++) {
      const m = y * 12;
      const withoutPlanY = monthly * m;
      const withPlanY = m === 0 ? 0 : monthly * ((Math.pow(1 + monthlyRate, m) - 1) / monthlyRate) * (1 + monthlyRate);
      data.push({
        year: y,
        sinPlan: Math.round(withoutPlanY),
        conPlan: Math.round(withPlanY)
      });
    }

    return {
      totalContributed: totalContrib,
      withoutPlan: finalWithoutPlan,
      withPlan: finalWithPlanPlusBonus,
      loyaltyBonus: loyaltyBonusAmount,
      gap: finalGap,
      chartData: data
    };
  }, [monthly, years]);

  return (
    <div className="optimaxx-comparator" data-testid="optimaxx-comparator">
      <div className="comparator-header">
        <div className="comparator-badge">
          <Sparkles className="w-4 h-4" />
          <span>¿Y si lo automatizas?</span>
        </div>
        <h3 className="comparator-title">El dinero que se te escapa vs. el que podrías tener</h3>
        <p className="comparator-subtitle">
          Mueve los sliders y comprueba la diferencia. Spoiler: vas a querer arrancar mañana 😏
        </p>
      </div>

      {/* Sliders */}
      <div className="comparator-controls">
        <div className="control-group">
          <div className="control-row">
            <label htmlFor="comparator-monthly" className="control-label">Aportación mensual</label>
            <span className="control-value">{formatMoney(monthly)}</span>
          </div>
          <input
            id="comparator-monthly"
            type="range"
            min="500"
            max="10000"
            step="500"
            value={monthly}
            onChange={(e) => setMonthly(parseInt(e.target.value, 10))}
            className="control-slider"
            data-testid="comparator-monthly-slider"
          />
          <div className="control-hints">
            <span>$500</span>
            <span>$10,000</span>
          </div>
        </div>

        <div className="control-group">
          <div className="control-row">
            <label htmlFor="comparator-years" className="control-label">Horizonte de tiempo</label>
            <span className="control-value">{years} años</span>
          </div>
          <input
            id="comparator-years"
            type="range"
            min="5"
            max="30"
            step="1"
            value={years}
            onChange={(e) => setYears(parseInt(e.target.value, 10))}
            className="control-slider"
            data-testid="comparator-years-slider"
          />
          <div className="control-hints">
            <span>5 años</span>
            <span>30 años</span>
          </div>
        </div>
      </div>

      {/* Result cards */}
      <div className="comparator-results">
        <div className="result-card result-bad">
          <div className="result-card-header">
            <TrendingDown className="w-6 h-6" />
            <span className="result-card-tag">SIN PLAN</span>
          </div>
          <p className="result-card-label">Lo que aportaste (si no lo gastaste antes)</p>
          <div className="result-card-amount" data-testid="comparator-without-plan">
            {formatMoney(withoutPlan)}
          </div>
          <p className="result-card-note">Cero rendimiento. Cero bono. Cero protección. 🥲</p>
        </div>

        <div className="result-card result-good">
          <div className="result-card-header">
            <TrendingUp className="w-6 h-6" />
            <span className="result-card-tag">CON OPTIMAXX PLUS</span>
          </div>
          <p className="result-card-label">Tu dinero con interés compuesto + bono</p>
          <div className="result-card-amount" data-testid="comparator-with-plan">
            {formatMoney(withPlan)}
          </div>
          <p className="result-card-note">
            Incluye bono de fidelidad: <strong>{formatMoney(loyaltyBonus)}</strong> al mes 12
          </p>
        </div>
      </div>

      {/* Gap highlight */}
      <div className="comparator-gap" data-testid="comparator-gap">
        <span className="gap-label">Te estás perdiendo</span>
        <span className="gap-amount">{formatMoney(gap)}</span>
        <span className="gap-note">Solo por seguir diciendo "mañana empiezo" 🙃</span>
      </div>

      {/* Chart */}
      <div className="comparator-chart">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData} margin={{ top: 10, right: 15, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="conPlanGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#39FF14" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#39FF14" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="sinPlanGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF4757" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#FF4757" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis dataKey="year" stroke="#888" tick={{ fontSize: 11 }} label={{ value: 'Años', position: 'insideBottom', offset: -5, fill: '#888', fontSize: 11 }} />
            <YAxis stroke="#888" tickFormatter={formatCompact} tick={{ fontSize: 11 }} width={60} />
            <Tooltip
              contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #39FF14', borderRadius: '8px' }}
              formatter={(value, name) => [formatMoney(value), name === 'conPlan' ? 'Con Optimaxx' : 'Sin plan']}
              labelFormatter={(label) => `Año ${label}`}
            />
            <Legend
              verticalAlign="top"
              height={28}
              iconType="circle"
              formatter={(value) => (value === 'conPlan' ? 'Con Optimaxx Plus' : 'Sin plan')}
            />
            <Area type="monotone" dataKey="sinPlan" stroke="#FF4757" strokeWidth={2.5} fill="url(#sinPlanGrad)" isAnimationActive animationDuration={1200} />
            <Area type="monotone" dataKey="conPlan" stroke="#39FF14" strokeWidth={3} fill="url(#conPlanGrad)" isAnimationActive animationDuration={1500} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Final CTA */}
      <div className="comparator-cta">
        <p className="comparator-cta-text">
          <strong>{formatMoney(monthly)}/mes</strong> hoy = <strong className="cta-highlight">{formatMoney(withPlan)}</strong> en {years} años.
          No es magia, es interés compuesto + disciplina obligada.
        </p>

        {/* Share buttons — viral conversion */}
        <ShareButtons
          text={buildComparatorShareText({ monthly, years, gap, withPlan })}
          label="Comparte tu resultado"
          testIdPrefix="comparator-share"
        />

        <button
          type="button"
          onClick={onCtaClick}
          className="comparator-cta-button"
          data-testid="comparator-cta-button"
        >
          Quiero arrancar mi plan ahora
        </button>
        <p className="comparator-cta-disclaimer">
          * Proyección con rendimiento promedio histórico de 8% anual. Resultados reales pueden variar según el portafolio elegido y condiciones de mercado.
        </p>
      </div>
    </div>
  );
};

export default OptimaxxComparator;
